import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Box,
  Paper,
  TableContainer,
  Button,
  Tabs,
  Tab
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import './TeamsOverviewTable.css';
import { useNavigate } from "react-router-dom";
import { useDivision } from '../../hooks/useDivision';
import { useTeam } from '../../hooks/useTeam';
import { useEventByRole } from '../../hooks/useEvent';
import { useCurrentUser } from '../../hooks/useAuth';

const headCells = [
  { id: 'name', label: 'Name', numeric: false },
  { id: 'eventsCount', label: 'Total Events', numeric: true },
  { id: 'activeEvents', label: 'Go To Events', numeric: true },
];

export default function TeamsOverviewTable() {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [activeTab, setActiveTab] = useState(0);
  const user = useCurrentUser();
  const navigate = useNavigate();

  const { data: divisions } = useDivision();
  const { data: teams } = useTeam();
  const { data: events } = useEventByRole({ user });

  const handleViewEvents = (item) => {
    const filteredEvents = events?.filter(event => 
      activeTab === 0 
        ? event.division_id === item.id  // For divisions tab
        : event.team_id === item.id      // For teams tab
    ) || [];

    navigate('/team-events', { 
      state: { 
        team: {
          id: item.id,
          name: item.name,
          events: filteredEvents,
          isDivision: activeTab === 0
        }
      } 
    });
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getEventCount = (id, isDivision = false) => {
    if (!events) return 0;
    return events.filter(event => isDivision ? event.division_id === id : event.team_id === id).length;
  };

  const renderTableContent = () => {
    const data = activeTab === 0 ? divisions : teams;
    if (!data) return null;

    return data.map((item, index) => (
      <TableRow
        hover
        key={index}
        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
      >
        <TableCell>{item.name}</TableCell>
        <TableCell align="center">{getEventCount(item.id, activeTab === 0)}</TableCell>
        <TableCell align="center">
          <Button 
            variant="contained"
            size="small"
            onClick={() => handleViewEvents(item)}
            sx={{ 
              backgroundColor: '#952264',
              '&:hover': {
                backgroundColor: '#7a1c52'
              }
            }}
          >
            View Events 
          </Button>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Paper className="TeamsTableParent">
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="teams and divisions tabs">
          <Tab label="Divisions" />
          <Tab label="Teams" />
        </Tabs>
      </Box>
      <TableContainer className="tableContainer">
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {headCells.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  align={headCell.numeric ? 'center' : 'left'}
                  sortDirection={orderBy === headCell.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={() => handleRequestSort(headCell.id)}
                  >
                    {headCell.label}
                    {orderBy === headCell.id ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {renderTableContent()}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}